import type { Appearance } from '@stripe/stripe-js';

/**
 * Legacy Stripe Elements style format (pre-Payment Element)
 */
export interface LegacyStripeStyle {
  base?: {
    '::placeholder'?: {
      color?: string;
    };
    ':focus'?: Record<string, string>;
    ':hover'?: Record<string, string>;
    color?: string;
    fontFamily?: string;
    fontSize?: string;
    fontWeight?: string;
    lineHeight?: string;
  };
  complete?: Record<string, any>;
  fonts?: Array<{
    cssSrc?: string;
    family?: string;
    src?: string;
    style?: string;
    weight?: string;
  }>;
  invalid?: {
    color?: string;
    iconColor?: string;
  };
}

/**
 * Converts legacy Stripe Elements style format to the new Appearance API format.
 * 
 * The legacy format used `base`, `complete`, and `invalid` style objects.
 * The new Appearance API uses `theme`, `variables`, and `rules`.
 * 
 * @param legacyStyle - The legacy Stripe style object
 * @returns Appearance object compatible with the new Stripe Payment Element
 * 
 * @example
 * ```typescript
 * const legacyStyle = {
 *   base: {
 *     color: '#333333',
 *     fontFamily: 'proxima-nova',
 *     fontSize: '14px',
 *     '::placeholder': { color: '#b3b3b3' }
 *   },
 *   invalid: {
 *     color: '#df1b41',
 *     iconColor: '#df1b41'
 *   }
 * };
 * 
 * const appearance = convertLegacyStripeStyle(legacyStyle);
 * // Returns an Appearance object with variables and rules
 * ```
 * 
 * @note
 * - Font loading via `fonts.cssSrc` is not directly supported in Appearance API.
 *   You should load these fonts separately in your HTML or via a style tag.
 * - Some legacy style properties may not have direct equivalents and are omitted.
 */
export const convertLegacyStripeStyle = (
  legacyStyle: LegacyStripeStyle
): Appearance => {
  const appearance: Appearance = {
    theme: 'flat',
    variables: {},
    rules: {},
  };

  if (!legacyStyle) {
    return appearance;
  }

  const { base, invalid } = legacyStyle;

  if (base) {
    const variables: Record<string, string> = {};

    if (base.color) {
      variables.colorText = base.color;
    }

    if (base.fontFamily) {
      // Add fallback fonts if not already included
      const { fontFamily } = base;
      if (!fontFamily.includes(',')) {
        variables.fontFamily = `${fontFamily}, Helvetica, Arial, sans-serif`;
      } else {
        variables.fontFamily = fontFamily;
      }
    }

    if (base.fontSize) {
      variables.fontSizeBase = base.fontSize;
    }

    if (base.fontWeight) {
      variables.fontWeightNormal = base.fontWeight;
    }

    if (base.lineHeight) {
      variables.fontLineHeight = base.lineHeight;
    }

    appearance.variables = variables;

    const rules: Record<string, Record<string, string>> = {};

    if (base['::placeholder']?.color) {
      rules['.Input::placeholder'] = {
        color: base['::placeholder'].color,
      };
    }

    if (base[':hover']) {
      rules['.Input:hover'] = base[':hover'];
    }

    if (base[':focus']) {
      rules['.Input:focus'] = base[':focus'];
    }

    appearance.rules = rules;
  }

  if (invalid) {
    if (!appearance.variables) {
      appearance.variables = {};
    }
    if (!appearance.rules) {
      appearance.rules = {};
    }

    if (invalid.color) {
      appearance.variables.colorDanger = invalid.color;
    }

    if (invalid.iconColor) {
      appearance.variables.colorDanger = invalid.iconColor;
    }
  }

  return appearance;
}
